import Ext from "Ext";
import UsersSelectorPanel from "./at_users_selector_panel";
import AccessRightFilter from "../hierarchySelector/userSelector/accessRight/AccessRightFilter";
import AccessRightUserGroupNodeRenderer from "../hierarchySelector/userSelector/accessRight/AccessRightUserGroupNodeRenderer";
import AccessRightUserNodeRenderer from "../hierarchySelector/userSelector/accessRight/AccessRightUserNodeRenderer";
import AccessRightEmptyMessageBuilder from "../hierarchySelector/userSelector/emptyMessagesBuilders/AccessRightEmptyMessageBuilder";
import ComplicateUsersAndGroupsSelectionHelper from "../controls/selectorControls/selectionHelpers/complicate_users_and_groups_selection_helper";
import GroupType from "../enums/GroupType";
import { escapeHtmlSymbols } from "../atUtils";
import { disableTextSelection } from "../atBrowserUtils";
import $ from 'jquery'

/**
 * Selector panel for the users list.
 *
 * Depends on:
 * <code>/js/selectorControls/at_users_selector_panel.js</code>
 * <code>/js/message_resources.js</code> with prefixes "users_selector", "mail_notifications.users_selector"
 * <code>/js/controls/selectorControls/at_tree_node_helper.js</code>
 * <code>/js/controls/selectorControls/at_user_tree_node_helper.js</code>
 * <code>/js/controls/selectorControls/at_ar_user_tree_node_helper.j</code>
 *
 * @constructor
 */
const AccessRightsUsersSelectorPanel = Ext.extend(UsersSelectorPanel, {

    /**
     * @cfg {AccessRight} accessRight determines whether item should be shown based on access right property value.
     *
     */
    accessRight: null,

    /**
     * @cfg {Boolean} showWithoutRight true to display items which has no right specified by
     *                @see AccessRightsUsersSelectorPanel.accessRight, false to not.
     */
    showWithoutRight: false,

    /**
     * @cfg {String} message key to be shown if <code>item's</code> rights do not conform
     *                                   <code>accessRight</code>
     */
    insufficientRightsMessageKey: "common.users_selector.access_right.insufficient_rights",

    emptyMessageBuilderCtor: AccessRightEmptyMessageBuilder,

    /**
     * @cfg {TreeNodeSet} set of all users with specified access right in selector
     */
    allAccessRightGrantedSetForUserGroups: null,
    allAccessRightGrantedSetForTimeZoneGroups: null,

    createGenericOptions: function()
    {
        this.genericOptions = [
            new Ext.form.Radio({
            boxLabel: escapeHtmlSymbols(this.accessRight.getSelectAllMessage()),
            name: this.realOptionsName,
            inputValue: this.allItemsOptionValue
          })
        ];
    },

    createSelectionFilters: function()
    {
        AccessRightsUsersSelectorPanel.superclass.createSelectionFilters.call(this);

        this.accessRightFilterCheckBox = new Ext.form.Checkbox({
            boxLabel: escapeHtmlSymbols(this.accessRight.getFilterMessage()),
            cls: 'selectionFilter'
        })

        var context = this;
        this.accessRightFilterCheckBox.on("check", function(checkbox, checked) {
            context.applyUsersWithoutGrantedAccessRightFilter(context,checked, true);
        }, this);
    },

    applyUsersWithoutGrantedAccessRightFilter: function(context, showWithoutRight, isFirePanelChange) {
        context.treeActionHandler(function() {context.selectorFilter.setShowUsersWithoutGrantedAccessRight(showWithoutRight)}, isFirePanelChange, true);
    },

    /**
     * @see AT.SelectorPanel
     */
    initComponent: function()
    {
        AccessRightsUsersSelectorPanel.superclass.initComponent.call(this);
        this.updateState();
    },

    /**
     * Sets initial value for filer check boxes
     * @param showInactive value for Show Disabled Checkbox
     * @param showWithoutRight value for Access Right Filter Checkbox
     */
    setFilterFlag: function ( showInactive, showWithoutRight )
    {
        AccessRightsUsersSelectorPanel.superclass.setFilterFlag.call(this, showInactive);
        this.showWithoutRight = showWithoutRight;
    },

    /**
     * @see AT.GenericSelectorPanel
     */
    updateState: function() {
        AccessRightsUsersSelectorPanel.superclass.updateState.call(this);
        if (this.isAlreadyShown()) {
            // if they don`t equal than on('check',..) event emitted and filter applied
            if(this.getShowWithoutRight() == this.showWithoutRight)
                this.applyUsersWithoutGrantedAccessRightFilter(this, this.showWithoutRight, false);
            else
                this.accessRightFilterCheckBox.setValue(this.showWithoutRight);
        }
    },

    setPredefinedParameters: function(state) {
        this.showWithoutRight = state.showWithoutRight;
        AccessRightsUsersSelectorPanel.superclass.setPredefinedParameters.call(this, state);
    },

    /**
     * @see AT.GenericSelectorPanel
     */
    getDetailsSection: function()
    {
        return [this.selectorTree, this.showInactiveCheckboxContainer, this.accessRightFilterCheckBox];
    },

    setSelectorFilter: function() {
        var allAccessRightGrantedSet = this.groupType == GroupType.TIME_ZONE_GROUP ?
            this.allAccessRightGrantedSetForTimeZoneGroups : this.allAccessRightGrantedSetForUserGroups;
        this.selectorFilter = new AccessRightFilter( this.selectorTree, this.searchIndex, this.allActiveSet,
                                                        this.accessRight, allAccessRightGrantedSet );
    },

    getUserGroupNodeRenderer: function() {
        return new AccessRightUserGroupNodeRenderer( this.selectorTree, this.selectorFilter, this.getExpandCollapseCallback(), this.accessRight);
    },

    getUserNodeRenderer: function() {
        return new AccessRightUserNodeRenderer( this.selectorTree, this.selectorFilter, this.getSelectionHelper(), this.checkEmail, this.treeItemTooltipMessageParams, this.accessRight );
    },

    makeSelectionHelper: function() {
        this.selectionHelper = new ComplicateUsersAndGroupsSelectionHelper( {
                                                                          userSelectorGroupsStrategy: this.userSelectorGroupsStrategy,
                                                                          accessRight: this.accessRight,
                                                                          maxLength: this.labelMaxChars,
                                                                          checkEmail: this.checkEmail
                                                                      });
    },

    checkAndProcessHiddenNodeSelection: function(treeNode) {
        AccessRightsUsersSelectorPanel.superclass.checkAndProcessHiddenNodeSelection.call(this, treeNode);
        var context = this;
        if (this.checkHiddenNodeSelectionTemplate(treeNode, function() {return !context.accessRight.isGranted(treeNode.getValue().accessRight)}))
        {
            this.accessRightFilterCheckBox.setValue(true);
            this.selectorFilter.setShowUsersWithoutGrantedAccessRight( true );
        }
    },

    createEmptyMessageBuilder: function() {
        return new AccessRightEmptyMessageBuilder(this.userSelectorGroupsStrategy, this.accessRight);
    },

    disableTextSelectionForNeededElements: function() {
        AccessRightsUsersSelectorPanel.superclass.disableTextSelectionForNeededElements.call(this);
        disableTextSelection(this.accessRightFilterCheckBox.labelEl.dom);
    },

    /**
     * @see AT.SelectorPanel
     */
    getFilterCallback: function() {
        var context = this;
        return function(user) {
            return context.accessRight.isGranted(user.accessRight);
        }
    },

    getShowWithoutRight: function()
    {
        return this.accessRightFilterCheckBox.getValue();
    },

    createDefaultState: function ()
    {
        return Ext.applyIf ({  showWithoutRight: false },
            AccessRightsUsersSelectorPanel.superclass.createDefaultState.call(this));
    },

    getState: function()
    {
        return Ext.applyIf ({  showWithoutRight: this.getShowWithoutRight() },
            AccessRightsUsersSelectorPanel.superclass.getState.call(this));
    },

    loadState: function(state, doNotUpdateGroupType)
    {
        this.showWithoutRight = state.showWithoutRight;
        AccessRightsUsersSelectorPanel.superclass.loadState.call(this, state, doNotUpdateGroupType);
    },

    collectCurrentSettings: function()
    {
        var settings = AccessRightsUsersSelectorPanel.superclass.collectCurrentSettings.call(this);
        settings.showWithoutRight = this.getShowWithoutRight();
        return settings;
    },

    getStateFromSettings: function(settings)
    {
        var state = AccessRightsUsersSelectorPanel.superclass.getStateFromSettings.call(this,settings);
        state.showWithoutRight = settings.showWithoutRight;
        return state;
    }

});

Ext.reg("at_users_selector_panel", AccessRightsUsersSelectorPanel);

export default AccessRightsUsersSelectorPanel;


// WEBPACK FOOTER //
// ./web/js/usersSelector/at_user_selector_panel_with_ar.js